import type { Point } from "./pathGeometry";

export interface CubicSegment {
  control1: Point;
  control2: Point;
  to: Point;
}

const TAU = Math.PI * 2;

export function arcToCubics(
  from: Point,
  rx: number,
  ry: number,
  xAxisRotationDegrees: number,
  largeArc: boolean,
  sweep: boolean,
  to: Point,
): CubicSegment[] {
  if (from.x === to.x && from.y === to.y) {
    return [];
  }

  const phi = (xAxisRotationDegrees * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  const x1p = (cosPhi * (from.x - to.x)) / 2 + (sinPhi * (from.y - to.y)) / 2;
  const y1p = (-sinPhi * (from.x - to.x)) / 2 + (cosPhi * (from.y - to.y)) / 2;

  let rxAbs = Math.abs(rx);
  let ryAbs = Math.abs(ry);
  const lambda = (x1p * x1p) / (rxAbs * rxAbs) + (y1p * y1p) / (ryAbs * ryAbs);
  if (lambda > 1) {
    const scale = Math.sqrt(lambda);
    rxAbs *= scale;
    ryAbs *= scale;
  }

  const rxSq = rxAbs * rxAbs;
  const rySq = ryAbs * ryAbs;
  const x1pSq = x1p * x1p;
  const y1pSq = y1p * y1p;
  let factor = Math.sqrt(Math.max(0, (rxSq * rySq - rxSq * y1pSq - rySq * x1pSq) / (rxSq * y1pSq + rySq * x1pSq)));
  if (largeArc === sweep) {
    factor = -factor;
  }

  const cxp = (factor * rxAbs * y1p) / ryAbs;
  const cyp = (-factor * ryAbs * x1p) / rxAbs;
  const cx = cosPhi * cxp - sinPhi * cyp + (from.x + to.x) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (from.y + to.y) / 2;

  const angle = (ux: number, uy: number, vx: number, vy: number) => {
    const dot = ux * vx + uy * vy;
    const len = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
    let cos = clamp(dot / len, -1, 1);
    const sign = ux * vy - uy * vx < 0 ? -1 : 1;
    return sign * Math.acos(cos);
  };

  const theta1 = angle(1, 0, (x1p - cxp) / rxAbs, (y1p - cyp) / ryAbs);
  let deltaTheta = angle(
    (x1p - cxp) / rxAbs,
    (y1p - cyp) / ryAbs,
    (-x1p - cxp) / rxAbs,
    (-y1p - cyp) / ryAbs,
  );
  if (!sweep && deltaTheta > 0) {
    deltaTheta -= TAU;
  } else if (sweep && deltaTheta < 0) {
    deltaTheta += TAU;
  }

  const segments = Math.max(1, Math.ceil(Math.abs(deltaTheta) / (Math.PI / 2)));
  const segmentDelta = deltaTheta / segments;
  const segmentsOut: CubicSegment[] = [];
  let current = from;

  for (let index = 0; index < segments; index += 1) {
    const startTheta = theta1 + segmentDelta * index;
    const endTheta = startTheta + segmentDelta;
    const alpha = (4 / 3) * Math.tan(segmentDelta / 4);

    const cosStart = Math.cos(startTheta);
    const sinStart = Math.sin(startTheta);
    const cosEnd = Math.cos(endTheta);
    const sinEnd = Math.sin(endTheta);

    const e1x = cx + rxAbs * (cosPhi * cosStart - sinPhi * sinStart);
    const e1y = cy + ryAbs * (sinPhi * cosStart + cosPhi * sinStart);
    const e2x = cx + rxAbs * (cosPhi * cosEnd - sinPhi * sinEnd);
    const e2y = cy + ryAbs * (sinPhi * cosEnd + cosPhi * sinEnd);

    const dx1 = -rxAbs * (cosPhi * sinStart + sinPhi * cosStart);
    const dy1 = -ryAbs * (sinPhi * sinStart - cosPhi * cosStart);
    const dx2 = -rxAbs * (cosPhi * sinEnd + sinPhi * cosEnd);
    const dy2 = -ryAbs * (sinPhi * sinEnd - cosPhi * cosEnd);

    const control1 = { x: current.x + alpha * dx1, y: current.y + alpha * dy1 };
    const control2 = { x: e2x - alpha * dx2, y: e2y - alpha * dy2 };
    const end = { x: e2x, y: e2y };
    segmentsOut.push({ control1, control2, to: end });
    current = end;
  }

  return segmentsOut;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
